import React, { useState } from 'react';
import { Box, Container, TextField, Button, Typography, Grid, Checkbox, FormControlLabel, IconButton, InputAdornment, OutlinedInput, FormControl, FormHelperText, Link } from '@mui/material';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ConfirmationPopup from '../../Utilities/ConfirmationPopup';
import { generalConfig } from '../../../configs/common';

export default function LoginForm() {
  const [openPopup, setOpenPopup] = useState(false);
  const [formData, setFormData] = useState({ clientId: '', username: '', password: '' });
  const [keepMeLoggedIn, setKeepMeLoggedIn] = useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  const [errors, setErrors] = useState({ clientId: false, username: false, password: false });
  const navigate = useNavigate();

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
  }

  const handleCheckboxChange = (e) => {
    setKeepMeLoggedIn(e.target.checked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { clientId, username, password } = formData;
    let newErrors = {};
    if (!clientId) {
      newErrors.clientId = true;
    }
    if (!username) {
      newErrors.username = true;
    }
    if (!password) {
      newErrors.password = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill in all required fields.");
      return;
    }

    try {
      const auth = btoa(`${clientId}/${username}/${password}`);
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_BASE_URL}/user/login`, { auth: auth, keepMeLoggedIn: keepMeLoggedIn });

      if (response.data.success) {
        localStorage.setItem('NCProAccessToken', response.data.accessToken);
        localStorage.setItem('NCProRefreshToken', response.data.refreshToken);

        toast.success(response.data.message || "Logged in Successfully");
        formData.clientId === generalConfig.SUPER_ADMIN_CLIENT_ID ? navigate('/users') : navigate('/ncpro/welcome');
      } else {
        toast.error(response.data.message || "Failed to login");
      }
    } catch (error) {
      console.error('Error during login:', error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Something went wrong");
    }
  }

  return (
    <Box>
      <Container sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '80vh', pt: 8, pb: 2 }}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ width: { xs: '100%', md: '80%' }, mb: 2 }}>
              <Typography variant="h1" sx={{ fontFamily: 'Lato, sans-serif', fontSize: '26px', fontWeight: '900', color: 'rgba(0, 0, 0, 1)', mb: 4 }}>
                Log in to your Account
              </Typography>
              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontFamily: 'Lato, sans-serif', mb: 1, fontWeight: '500', fontSize: '14px', color: 'rgba(0, 0, 0, 1)' }}>Client ID</Typography>
                  <TextField
                    className="login-page-input"
                    name="clientId"
                    value={formData.clientId}
                    onChange={handleChange}
                    placeholder="Enter Your Client ID"
                    fullWidth
                    error={errors.clientId}
                    helperText={errors.clientId ? "Client ID is required." : ""}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontFamily: 'Lato, sans-serif', mb: 1, fontWeight: '500', fontSize: '14px', color: 'rgba(0, 0, 0, 1)' }}>User Name</Typography>
                  <TextField
                    className="login-page-input"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    placeholder="Enter Your User Name"
                    fullWidth
                    error={errors.username}
                    helperText={errors.username ? "Username is required." : ""}
                    inputProps={{
                      readOnly: true,
                      onFocus: (e) => e.target.removeAttribute("readonly"),
                      autocomplete: "off",
                    }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: 'Lato, sans-serif', mb: 1, fontWeight: '500', fontSize: '14px', color: 'rgba(0, 0, 0, 1)' }}>Password</Typography>
                  <FormControl fullWidth error={errors.password} size="small">
                    <OutlinedInput
                      id="outlined-adornment-password"
                      size="medium"
                      className='login-page-input'
                      name="password"
                      value={formData.password}
                      sx={{ fontSize: "14px", fontFamily: "Lato, sans-serif", }}
                      onChange={handleChange}
                      type={showPassword ? 'text' : 'password'}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            onMouseUp={handleMouseUpPassword}
                            edge="end"
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      }
                      placeholder="Enter Your Password"
                    />
                    {errors.password && (
                      <FormHelperText>Password is required.</FormHelperText>
                    )}
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={keepMeLoggedIn}
                        onChange={handleCheckboxChange}
                      />
                    }
                    label="Keep me logged in"
                    componentsProps={{
                      typography: {
                        sx: {
                          fontWeight: 400,
                          fontSize: '14px',
                          color: 'rgba(84, 84, 84, 1) !important',
                        }
                      }
                    }}
                  />
                  <Typography className='forgot-password-label' sx={{ cursor: 'pointer', color: 'rgba(55, 102, 243, 1)' }} onClick={() => setOpenPopup(true)}>
                    Forgot Password?
                  </Typography>
                </Box>
                <Box sx={{ py: 2 }}>
                  <Button
                    type="submit"
                    fullWidth
                    sx={{ fontFamily: 'Lato, sans-serif', fontWeight: 700, fontSize: '14px', color: ' rgba(255, 255, 255, 1)', py: 1, background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)', textTransform: 'none' }}
                    variant="contained"
                  >
                    Login
                  </Button>
                </Box>
              </form>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Link href="/">
              <img src="/images/loginImage.svg" alt="NC Pro" style={{ maxWidth: "100%", height: "auto" }} />
            </Link>
          </Grid>
        </Grid>
      </Container>
      <ConfirmationPopup
        open={openPopup}
        label="ForgotPassword"
        setOpen={setOpenPopup}
        onConfirm={() => navigate('/forgot-password')}
      />
    </Box>
  )
}